import { Table } from "antd";
import "./DataTable.scss";
import type { ColumnsType } from "antd/es/table";
import { type CSSProperties, type HTMLAttributes, type ReactNode, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { DataTableActions } from "~components/DataTable/components/DataTableActions/DataTableActions.tsx";
import { DataTableHeader } from "~components/DataTable/components/DataTableHeader/DataTableHeader.tsx";
import { PaginationTotal } from "~components/DataTable/components/Pagination/PaginationTotal.tsx";
import type { BaseRowActions } from "~components/DataTable/types/baseRowActions.ts";
import type { CustomRowActions } from "~components/DataTable/types/customRowActions.ts";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { ExportConfig } from "~components/DataTable/types/exportConfig.ts";
import type { FiltersConfig } from "~components/DataTable/types/filtersConfig.ts";
import type { ImportConfig } from "~components/DataTable/types/importConfig.ts";
import type { PaginationConfig } from "~components/DataTable/types/paginationConfig.ts";
import type { RowNavigationConfig } from "~components/DataTable/types/rowNavigationConfig.ts";
import type { SearchConfig } from "~components/DataTable/types/searchConfig.ts";
import { useAppSettings } from "~context/AppSettingsContext/AppSettingsContext.tsx";
import type { PermissionNames } from "~enums/permissions.ts";
import type { DataTableConfig } from "~types/dataTableConfig.ts";
import { useRowNavigation } from "./hooks/useRowNavigation";
import { useTableColumns } from "./hooks/useTableColumns";

type SortConfig = {
  sorterLocalStorageKey: string;
  defaultSorter: { columnKey: string; order: "ascend" | "descend" };
  onSortChange?: (value: string) => void;
};

type Props<T = unknown> = {
  columns: DataTableColumn<T>[];
  rowKey: string;
  rowNameKey?: string;
  dataSource: T[];
  title?: ReactNode;
  headerCustomActions?: ReactNode;
  columnChooserLocalStorageKey: string;
  columnSortConfig?: SortConfig;
  isDataLoading?: boolean;
  permissionNames: Array<PermissionNames>;
  filtersConfig?: FiltersConfig;
  searchConfig?: SearchConfig;
  searchPlaceholderIntlKey?: string;
  handleAdd?: () => void;
  addButtonIntlKey?: string;
  importConfig?: ImportConfig;
  handleRefresh?: () => Promise<void>;
  exportConfig?: ExportConfig;
  paginationConfig?: PaginationConfig;
  onMassEditModeChangedHandler?: (checked: boolean) => void;
  massEditMode?: boolean;
  dataTableUiConfiguration: DataTableConfig;
  onRowClick?: (record: T) => HTMLAttributes<any>;
  rowNavigation?: RowNavigationConfig<T>;
  rowActions?: {
    base?: BaseRowActions<T>;
    custom?: CustomRowActions<T>;
  };
};

export const DataTable = <T extends object = any>({
  columns,
  columnSortConfig,
  dataSource,
  searchConfig,
  handleAdd,
  permissionNames,
  addButtonIntlKey,
  searchPlaceholderIntlKey,
  importConfig,
  columnChooserLocalStorageKey,
  title,
  headerCustomActions,
  exportConfig,
  filtersConfig,
  handleRefresh,
  onMassEditModeChangedHandler,
  massEditMode,
  paginationConfig,
  isDataLoading,
  rowKey,
  rowNameKey,
  dataTableUiConfiguration,
  onRowClick,
  rowNavigation,
  rowActions,
}: Props<T>) => {
  const tableColumns = columns.map((column) => ({ ...column, key: column.key || column.dataIndex })) as DataTableColumn<T>[];
  const { tableDensity } = useAppSettings();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const onRefresh = async () => {
    setIsRefreshing(true);
    try {
      await handleRefresh?.();
    } finally {
      setIsRefreshing(false);
    }
  };

  const { getRowProps, getRowClassName } = useRowNavigation<T>({ onRowClick, rowNavigation, massEditMode });

  useEffect(() => {
    if (location.state?.search) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.search]);

  const { columnsToShow, setSelectedColumns, setSortColumn } = useTableColumns<T>({
    columnChooserConfig: {
      columnChooserLocalStorageKey: columnChooserLocalStorageKey,
      tableConfiguration: dataTableUiConfiguration.columnsConfig,
    },
    columnSortConfig: columnSortConfig,
    tableColumns: tableColumns,
    rowNameKey: rowNameKey || rowKey,
    rowActions,
    permissionNames: permissionNames,
    massEditMode,
  });

  const itemsPerPageLabel = formatMessage({
    id: "data.table.pagination.itemsPerPage",
  });

  /*
    DC The API does not return a total count for most queries,
    so this code adds +1 to the length to display the Next button when hasNext is true.
   */
  const total =
    paginationConfig?.total ||
    (paginationConfig?.hasNext ? paginationConfig.offset + dataSource.length + 1 : (paginationConfig?.offset || 0) + dataSource.length);

  return (
    <div
      className="data-table-container"
      style={
        {
          "--items-per-page-label": `"${itemsPerPageLabel}"`,
        } as CSSProperties
      }
    >
      <div className="data-table-container-header">
        {title && <div className="data-table-title">{title}</div>}
        <DataTableActions
          exportConfig={exportConfig}
          importConfig={importConfig}
          handleAdd={handleAdd}
          permissionNames={permissionNames}
          addButtonIntlKey={addButtonIntlKey}
          customActions={headerCustomActions}
        />
      </div>
      <DataTableHeader
        filtersConfig={filtersConfig}
        handleAdd={handleAdd}
        searchConfig={searchConfig}
        permissionNames={permissionNames}
        tableColumns={tableColumns}
        setSelectedColumns={setSelectedColumns}
        columnChooserLocalStorageKey={columnChooserLocalStorageKey}
        dataTableUiConfiguration={dataTableUiConfiguration}
        searchPlaceholderIntlKey={searchPlaceholderIntlKey}
        onMassEditModeChangedHandler={onMassEditModeChangedHandler}
        massEditMode={massEditMode}
        handleRefresh={handleRefresh && onRefresh}
        isRefreshing={isRefreshing}
      />
      <Table<T>
        columns={columnsToShow as ColumnsType<T>}
        rowKey={rowKey}
        onChange={(_, __, sorter) => setSortColumn(sorter)}
        dataSource={dataSource}
        rootClassName={"data-table"}
        scroll={{ x: "max-content" }}
        sticky={true}
        loading={isDataLoading || isRefreshing}
        tableLayout="fixed"
        size={tableDensity}
        onRow={getRowProps}
        rowClassName={getRowClassName}
        pagination={{
          size: "default",
          current: paginationConfig?.currentPage,
          pageSize: paginationConfig?.pageSize,
          onChange: paginationConfig?.handlePaginationChange,
          pageSizeOptions: paginationConfig?.pageSizeOptions,
          rootClassName: "data-table-pagination",
          showSizeChanger: true,
          showTitle: false,
          showTotal: () => <PaginationTotal paginationConfig={paginationConfig} />,
          total: total,
          itemRender: (_, type, originalElement) => {
            if (type === "next") {
              return <i className="fa-solid fa-chevron-right" style={{ fontSize: 14 }}></i>;
            }
            if (type === "prev") {
              return <i className="fa-solid fa-chevron-left"></i>;
            }
            return originalElement;
          },
        }}
      />
    </div>
  );
};
